import React from 'react';
import { alpha } from '@mui/material/styles';
import { CANVAS_W, CANVAS_H } from '../constants';

// ── Constants ─────────────────────────────────────────────────────────────────
const H           = 10;        // handle square half-size in SVG canvas px
const GIZMO_COLOR = '#1075BA';
const GIZMO_FILL  = '#fff';

// ── Bezier path regeneration (mirrors PenTool.pointsToPath) ──────────────────
function bezierPointsToPathD(pts, canvasW = CANVAS_W, canvasH = CANVAS_H) {
  if (!pts || pts.length < 2) return '';
  const tx = p => (p.x / 100) * canvasW;
  const ty = p => (p.y / 100) * canvasH;
  let d = `M ${tx(pts[0])},${ty(pts[0])}`;
  for (let i = 1; i < pts.length; i++) {
    const prev = pts[i - 1];
    const curr = pts[i];
    const cp1  = prev.cp2 || prev;
    const cp2  = curr.cp1 || curr;
    if (prev.cp2 || curr.cp1) {
      d += ` C ${tx(cp1)},${ty(cp1)} ${tx(cp2)},${ty(cp2)} ${tx(curr)},${ty(curr)}`;
    } else {
      d += ` L ${tx(curr)},${ty(curr)}`;
    }
  }
  return d + ' Z';
}

// ── getBounds ─────────────────────────────────────────────────────────────────
/**
 * Axis-aligned bounding box in % units.
 * For bezier polygons, includes ALL control points so the gizmo encloses the
 * full curve, not just the anchors.
 */
export function getBounds(hotspot, canvasW = CANVAS_W, canvasH = CANVAS_H) {
  if (hotspot.shape === 'rect' && hotspot.points?.[0] && hotspot.meta) {
    return {
      x: hotspot.points[0].x,
      y: hotspot.points[0].y,
      w: hotspot.meta.width  || 10,
      h: hotspot.meta.height || 10,
    };
  }

  if (hotspot.shape === 'circle' && hotspot.points?.[0]) {
    const r  = hotspot.meta?.radius || 5;
    const rY = r * (canvasW / canvasH); // radius in Y % space
    return {
      x: hotspot.points[0].x - r,
      y: hotspot.points[0].y - rY,
      w: r * 2,
      h: rY * 2,
    };
  }

  if (hotspot.shape === 'polygon') {
    // Collect all % coordinates: anchors + bezier control points
    const allPts = [];
    const anchors = hotspot.points || [];
    anchors.forEach(p => {
      allPts.push({ x: p.x, y: p.y });
      if (p.cp1) allPts.push({ x: p.cp1.x, y: p.cp1.y });
      if (p.cp2) allPts.push({ x: p.cp2.x, y: p.cp2.y });
    });
    if (allPts.length === 0) return null;
    const xs = allPts.map(p => p.x);
    const ys = allPts.map(p => p.y);
    const minX = Math.min(...xs), maxX = Math.max(...xs);
    const minY = Math.min(...ys), maxY = Math.max(...ys);
    return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
  }

  return null;
}

// ── translateHotspot ──────────────────────────────────────────────────────────
function clamp(v) { return Math.max(0, Math.min(100, v)); }

function translatePoint(p, dx, dy) {
  const moved = { ...p, x: clamp(p.x + dx), y: clamp(p.y + dy) };
  if (p.cp1) moved.cp1 = { x: clamp(p.cp1.x + dx), y: clamp(p.cp1.y + dy) };
  if (p.cp2) moved.cp2 = { x: clamp(p.cp2.x + dx), y: clamp(p.cp2.y + dy) };
  return moved;
}

export function translateHotspot(hotspot, dx, dy) {
  if (hotspot.shape === 'rect') {
    const p0 = hotspot.points?.[0];
    if (!p0) return hotspot;
    return {
      ...hotspot,
      points: [{ ...p0, x: clamp(p0.x + dx), y: clamp(p0.y + dy) }],
    };
  }
  if (hotspot.shape === 'circle') {
    const p0 = hotspot.points?.[0];
    if (!p0) return hotspot;
    return {
      ...hotspot,
      points: [{ ...p0, x: clamp(p0.x + dx), y: clamp(p0.y + dy) }],
    };
  }
  if (hotspot.shape === 'polygon') {
    if (!hotspot.points?.length) return hotspot;
    return { ...hotspot, points: hotspot.points.map(p => translatePoint(p, dx, dy)) };
  }
  return hotspot;
}

// ── applyResize ───────────────────────────────────────────────────────────────
export function applyResize(hotspot, handle, dx, dy) {
  if (hotspot.shape === 'rect') {
    const p0 = hotspot.points?.[0];
    if (!p0) return hotspot;
    let { x, y } = p0;
    let { width: w, height: h } = hotspot.meta;

    if (handle.includes('e')) { w = Math.max(2, w + dx); }
    if (handle.includes('s')) { h = Math.max(2, h + dy); }
    if (handle.includes('w')) { const nw = Math.max(2, w - dx); x = clamp(x + (w - nw)); w = nw; }
    if (handle.includes('n')) { const nh = Math.max(2, h - dy); y = clamp(y + (h - nh)); h = nh; }

    return { ...hotspot, points: [{ ...hotspot.points[0], x, y }], meta: { ...hotspot.meta, width: w, height: h } };
  }

  if (hotspot.shape === 'circle') {
    const dr = (Math.abs(dx) > Math.abs(dy) ? dx : dy);
    const r  = Math.max(1, (hotspot.meta?.radius || 5) + dr);
    return { ...hotspot, meta: { ...hotspot.meta, radius: r } };
  }

  return hotspot;
}

// ── SelectionGizmo ────────────────────────────────────────────────────────────
/**
 * Dashed bounding-box + 8 square resize handles rendered inside the SVG overlay.
 * onHandleMouseDown(e, handleKey) fires when a handle drag begins.
 * onBodyMouseDown(e) fires when the body (fill) is dragged — used to move.
 */
export function SelectionGizmo({ hotspot, onHandleMouseDown, onBodyMouseDown, canvasW = CANVAS_W, canvasH = CANVAS_H, disableBodyEvents = false }) {
  const b = getBounds(hotspot, canvasW, canvasH);
  if (!b) return null;

  const px = v => (v / 100) * canvasW;
  const py = v => (v / 100) * canvasH;

  const x  = px(b.x), y  = py(b.y);
  const w  = px(b.w), h  = py(b.h);
  const mx = x + w / 2, my = y + h / 2;

  const color = hotspot.glowColor || GIZMO_COLOR;

  const handles = [
    ['nw', x,     y,     'nw-resize'],
    ['n',  mx,    y,     'n-resize' ],
    ['ne', x + w, y,     'ne-resize'],
    ['e',  x + w, my,    'e-resize' ],
    ['se', x + w, y + h, 'se-resize'],
    ['s',  mx,    y + h, 's-resize' ],
    ['sw', x,     y + h, 'sw-resize'],
    ['w',  x,     my,    'w-resize' ],
  ];

  return (
    <g>
      {/* Invisible fill — drag target for moving the shape */}
      <rect
        x={x} y={y} width={w} height={h}
        fill="transparent"
        stroke="none"
        style={{ cursor: disableBodyEvents ? 'default' : 'move', pointerEvents: disableBodyEvents ? 'none' : 'all' }}
        onMouseDown={disableBodyEvents ? undefined : onBodyMouseDown}
      />

      {/* Outer dashed bounding box */}
      <rect
        x={x} y={y} width={w} height={h}
        fill="none"
        stroke={color}
        strokeWidth={1.5}
        strokeDasharray="6,4"
        rx={2}
        pointerEvents="none"
      />

      {/* Inner white ring for contrast */}
      <rect
        x={x - 1} y={y - 1} width={w + 2} height={h + 2}
        fill="none"
        stroke="rgba(255,255,255,0.55)"
        strokeWidth={1}
        rx={3}
        pointerEvents="none"
      />

      {/* 8 resize handles */}
      {handles.map(([key, cx, cy, cursor]) => (
        <rect
          key={key}
          x={cx - H / 2} y={cy - H / 2}
          width={H} height={H}
          rx={2}
          fill={GIZMO_FILL}
          stroke={color}
          strokeWidth={1.5}
          style={{ cursor }}
          onMouseDown={e => { e.stopPropagation(); onHandleMouseDown(e, key); }}
        />
      ))}
    </g>
  );
}